import type { LeagueSnapshot, MatchupReport, SlateGame } from "@averagejoesff/shared";
import { AppLink } from "../../components/AppLink";
import { formatRecord } from "../../lib/format";
import { matchupAnchorId } from "../matchups/series";
import type { PeopleLookup } from "../managers/people";
import { personName } from "../managers/people";

interface LeagueSlateProps {
  snapshot: LeagueSnapshot;
  /** The games to list; the standings in `snapshot` supply each side's record. */
  games: readonly SlateGame[];
  people: PeopleLookup;
  myId: string | null;
  /** This week's published Matchups report; a game with a card in it links there. */
  report: MatchupReport | null;
}

/**
 * Every game this week as a compact two-line card (a grid, so twelve teams
 * fit in two or three rows rather than six); the visitor's own game is marked.
 * When this week's Matchups report is published, each card links to that
 * game's card on the Matchups page.
 */
export function LeagueSlate({ snapshot, games, people, myId, report }: LeagueSlateProps) {
  const recordOf = (id: string) => {
    const r = snapshot.standings.find((s) => s.personId === id)?.record;
    return r ? formatRecord(r.wins, r.losses, r.ties) : null;
  };
  // Looked up by anchor, which ignores which side is listed first.
  const published = new Set(report?.matchups.map((m) => matchupAnchorId(m.managerIds)));

  return (
    <ul className="home-slate">
      {games.map((game) => {
        const mine = myId !== null && game.personIds.includes(myId);
        const className = mine ? "home-slate__game home-slate__game--mine" : "home-slate__game";
        const anchor = matchupAnchorId(game.personIds);
        const sides = game.personIds.map((id, side) => (
          <span key={id} className="home-slate__side">
            {/* Read as "A, 3–1, versus B, 2–2" rather than two unrelated rows. */}
            {side === 1 && <span className="visually-hidden">versus </span>}
            <span className="home-slate__name">{personName(people, id)}</span>
            <span className="home-slate__record">{recordOf(id)}</span>
          </span>
        ));
        return (
          <li key={game.personIds.join("-")}>
            {published.has(anchor) ? (
              <AppLink href={`/matchups?week=${snapshot.season}-${game.week}#${anchor}`} className={`${className} home-slate__game--link`}>
                {sides}
                <span className="visually-hidden"> — matchup details</span>
              </AppLink>
            ) : (
              <div className={className}>{sides}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
